import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TENANT_QUERY_KEY, useTenant } from '../../../contexts/TenantContext';
import useErrorHandler from '../../../hooks/useErrorHandler';
import apiClient from '../../../services/apiClientService';
import { Routes } from '../../../routes/RouteConstants';
import { COUPONS_QUERY_KEY } from './useGetCoupons';

/**
 * Creates a coupon, or with an id replaces every field of one. Every field can be changed at any
 * time: a booking already made keeps the price it was confirmed at. The tenant is refetched too,
 * since whether a coupon is redeemable today is on it, and the confirmation page reads that.
 */
const useSaveCoupon = () => {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  const mutation = useMutation({
    mutationFn: ({ id, coupon }) => (id
      ? apiClient.put(`/v1/tenants/${tenantId}/coupons/${id}`, coupon)
      : apiClient.post(`/v1/tenants/${tenantId}/coupons`, coupon)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COUPONS_QUERY_KEY, exact: true });
      queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEY });
    },
    onError: (error) => handleError(error, Routes.DASHBOARD_COUPONS),
  });

  const saveCoupon = (id, coupon, onSuccess) => mutation.mutate({ id, coupon }, { onSuccess });

  return { saveCoupon, isLoading: mutation.isPending };
};

export default useSaveCoupon;
